import React, { useState } from 'react';
import { JourneyStep, StepStatus } from '../../types/journey';
import { useLanguage } from '../../context/LanguageContext';
import './StepTimeline.css';

interface StepTimelineProps {
  steps: JourneyStep[];
  currentStepIndex: number;
  onStepSelect?: (stepIndex: number) => void;
}

export const StepTimeline: React.FC<StepTimelineProps> = ({
  steps,
  currentStepIndex,
  onStepSelect
}) => {
  const { t, uiLanguage } = useLanguage();
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  const getStepStatusClass = (status: StepStatus) => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'current':
        return 'current';
      case 'skipped':
        return 'skipped';
      case 'locked':
      default:
        return 'locked';
    }
  };

  const getStepNumber = (step: JourneyStep, index: number) => {
    if (step.status === 'completed') return '✓';
    if (step.status === 'skipped') return '⤴';
    return (index + 1).toString();
  };

  const isStepClickable = (status: StepStatus) => {
    return status === 'current' || status === 'completed';
  };

  const handleStepClick = (stepIndex: number, step: JourneyStep) => {
    if (isStepClickable(step.status)) {
      setSelectedStep(selectedStep === stepIndex ? null : stepIndex);
      if (onStepSelect) {
        onStepSelect(stepIndex);
      }
    }
  };

  // const displayStep = hoveredStep !== null ? hoveredStep : selectedStep;

  return (
    <div className="step-timeline-compact">
      <h2>{t('journey.progressTimeline')}</h2>
      
      {/* Compact one-line timeline */}
      <div className="timeline-line">
        {steps.map((step, index) => (
          <div key={step.id} className="timeline-step-wrapper">
            <div
              className={`timeline-point ${getStepStatusClass(step.status)} ${
                isStepClickable(step.status) ? 'clickable' : ''
              } ${index === currentStepIndex ? 'highlight' : ''}`}
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
              onClick={() => handleStepClick(index, step)}
              title={uiLanguage === 'zh' && step.title_zh ? step.title_zh : step.title}
            >
              <span className="point-number">
                {getStepNumber(step, index)}
              </span>
            </div>
            
            {/* Timeline name and time under each point */}
            <div className="timeline-point-info">
              <div className="timeline-point-title">{uiLanguage === 'zh' && step.title_zh ? step.title_zh : step.title}</div>
              <div className="timeline-point-time">
                {step.timeEstimateWeeks} {step.timeEstimateWeeks !== 1 ? t('journey.weeks') : t('journey.week')}
              </div>
            </div>
            
            {index < steps.length - 1 && (
              <div className={`timeline-connector ${step.status === 'completed' ? 'completed' : ''}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};